import { Component, OnInit, ViewChild, Input, ViewChildren, QueryList } from '@angular/core';
import { Observable, Subscriber, TeardownLogic, Subscription } from 'rxjs';
import { GoogleMap, MapInfoWindow, MapMarker, MapGeocoder, MapDirectionsService } from '@angular/google-maps';
import { CrudService } from 'src/app/services/crud.service';
import { UtilService } from 'src/app/services/util.service';
import { IonModal, LoadingController, Platform } from '@ionic/angular';
import { Estabelecimento, EstabelecimentoResponse, EstablishmentMarker } from 'src/models/estabelecimento.model';
import { Router } from '@angular/router';
import { mapLoadingProps } from 'src/utils/util.functions';
import { MapsService } from 'src/app/services/maps.service';
import { AuthService } from 'src/app/services/auth.service';
import { MotoristaMarker, MotoristaResponse } from 'src/models/motorista.model';
import { ResponseAPI } from 'src/models/user.model';
import { map } from 'rxjs/operators';
import { Events } from 'src/app/services/events-service.service';
import { listServices } from "src/models/services.model";
import { listLanguages, Languages } from "src/models/language.model";
const { setTimeout, setInterval } = window;

@Component({
    selector: 'app-dashboard-map',
    templateUrl: './dashboard-map.component.html',
    styleUrls: ['./dashboard-map.component.scss'],
    standalone: false
})
export class DashboardMapComponent implements OnInit {
  @ViewChild(MapInfoWindow) infoWindow: MapInfoWindow;
  @ViewChild(GoogleMap) googleMap: GoogleMap;
  @ViewChildren(MapMarker) motoristaMapMarker: QueryList<MapMarker>;
  @ViewChild(IonModal) modal: IonModal;
  @Input() driverId: string;
  @Input() latLngDirectionFrom: google.maps.LatLngLiteral;
  @Input() latLngDirectionTo: google.maps.LatLngLiteral;
  @Input() showEstablishmentMarkers: boolean = true;
  apiLoaded: boolean;
  services: { value: number; label: string }[] = listServices;
  languages: { value: number; label: string }[] = listLanguages;
  directionOptions: google.maps.DirectionsRendererOptions = {
    suppressMarkers: true,
  }
  Languages = Languages;
  mapOptions: google.maps.MapOptions = {
    center: { lat: 28.4297768, lng: -81.4713984 },
    zoom: 14.5,
    disableDefaultUI: true,
    clickableIcons: false,
    keyboardShortcuts: false,
    styles: [
      {
        "featureType": "administrative",
        "elementType": "geometry",
        "stylers": [
          {
            "visibility": "off"
          }
        ]
      },
      {
        "featureType": "administrative.land_parcel",
        "elementType": "labels",
        "stylers": [
          {
            "visibility": "off"
          }
        ]
      },
      {
        "featureType": "poi",
        "stylers": [
          {
            "visibility": "off"
          }
        ]
      },
      {
        "featureType": "poi",
        "elementType": "labels.text",
        "stylers": [
          {
            "visibility": "off"
          }
        ]
      },
      {
        "featureType": "road",
        "elementType": "labels.icon",
        "stylers": [
          {
            "visibility": "off"
          }
        ]
      },
      {
        "featureType": "road.local",
        "elementType": "labels",
        "stylers": [
          {
            "visibility": "off"
          }
        ]
      },
      {
        "featureType": "transit",
        "stylers": [
          {
            "visibility": "off"
          }
        ]
      }
    ]
  };

  establishmentMarkers: EstablishmentMarker[] = [];
  motoristaMarkers: MotoristaMarker[] = [];
  currentUserMarker: google.maps.MarkerOptions = {};
  restaurants: Estabelecimento[] = [];
  locationSubscription: Subscription;
  selectedRestaurant: Estabelecimento = {} as Estabelecimento;
  timeout: number;
  getDriverInterval: number;
  mapsServiceSubscribe: Subscription;
  private dataLoaded: boolean;

  public directionsResults$: Observable<google.maps.DirectionsResult | undefined>;
  watchId: number;

  constructor(
    private _crud: CrudService,
    private _util: UtilService,
    private _platform: Platform,
    private _loadCtrl: LoadingController,
    private _router: Router,
    private _mapsService: MapsService,
    private auth: AuthService,
    private _geocoder: MapGeocoder,
    private _mapDirectionsService: MapDirectionsService,
    private _events: Events) {
  }

  async ngOnInit() {
    await this._mapsService.loadGoogleMaps();
    this.apiLoaded = true;
    this.currentUserMarker = {
      draggable: false,
      clickable: false,
      position: {
        lat: 28.4297768,
        lng: -81.4713984,
      },
      icon: {
        url: '/assets/images/user-location.png',
        size: new google.maps.Size(50, 50),
        scaledSize: new google.maps.Size(50, 50),
      }
    } as google.maps.MarkerOptions;
    await this.loadData();

    this._events.subscribe('update:establishments', async () => {
      await this.loadData();
    });
    this._events.subscribe('clear:location', async () => {
      this.dataLoaded = false;
      await this.clearTimers();
      await this.clearWatches();
    });
  }

  public getServiceDescription(service: number) {
    return this.services.filter((s) => s.value === service)[0].label;
  }

  private async clearTimers() {
    await new Promise((resolve, reject) => {
      for (let index = 1000; index >= 0; index--) {
        clearTimeout(index);
        clearInterval(index);
      }
      resolve(true);
      reject(false);
    });
  }

  private async clearWatches() {
    navigator.geolocation.clearWatch(this.watchId);
    this.locationSubscription.unsubscribe();
  }

  private async loadData() {
    if (this.dataLoaded) {
      return;
    }
    await this.getLocationUser();
    if (this.showEstablishmentMarkers) {
      this.getEstablishments();
    }
    this.getDrivers();
    setTimeout(() => {
      this.recenterMap();
      if (this.driverId && this.latLngDirectionFrom && this.latLngDirectionTo) {
        this.getDirections();
      }
    }, 1000);
    this.dataLoaded = true;
  }

  private getDirections() {
    const request: google.maps.DirectionsRequest = {
      destination: this.latLngDirectionTo,
      origin: this.latLngDirectionFrom,
      travelMode: google.maps.TravelMode.DRIVING
    };
    this.directionsResults$ = this._mapDirectionsService.route(request).pipe(map(response => response.result));
  }

  public recenterMap() {
    this.googleMap.panTo(this.currentUserMarker.position);
    this.googleMap.googleMap.setZoom(14.5);
  }

  public zoomIn() {
    const currentZoom = this.googleMap.getZoom();
    this.googleMap.googleMap.setZoom(currentZoom + 1);
  }

  public zoomOut() {
    const currentZoom = this.googleMap.getZoom();
    this.googleMap.googleMap.setZoom(currentZoom - 1);
  }

  serviceModal() {
    console.log("serviceModal")
  }

  private getDrivers() {
    this.getDriverInterval = setInterval(() => {
      // this.getTrips();
      this._crud.getData(`motorista/v2/online/${this.auth.user.id}`).subscribe((response: MotoristaResponse[]) => {
        let temp: MotoristaMarker[] = [];
        response.filter(r => r.inTrip || (!r.inTrip && r.dataEntradaFila)).map(motorista => {
          temp.push({
            motorista: motorista,
            marker: {
              draggable: false,
              position: {
                lat: parseFloat(motorista.latitude),
                lng: parseFloat(motorista.longitude),
              },
              icon: {
                url: motorista.inTrip ? `/assets/images/map/car_current_trip.png?id=${motorista.id}` : `/assets/images/map/car_normal.png?id=${motorista.id}`,
                scaledSize: new google.maps.Size(45, 45, "px", "px"),
              } as google.maps.Icon,
              title: motorista.id,
            } as google.maps.MarkerOptions
          });
          const marker = this.motoristaMapMarker.find(e => e.marker.getTitle() === motorista.id);
          if (marker) {
            const markerElements = document.querySelectorAll(`[title="${motorista.id}"]`);
            markerElements.forEach(e => {
              const img = document.querySelectorAll(`[src*="${motorista.id}"]`)
              if (img.length) {
                img[0]["style"].transform = `rotate(${motorista.heading}deg)`;
              }
            });
          }
        });
        if (this.driverId) {
          this.motoristaMarkers = response.filter(e => e.id == this.driverId).map(motorista => {
            return {
              motorista: motorista,
              marker: {
                draggable: false,
                position: {
                  lat: parseFloat(motorista.latitude),
                  lng: parseFloat(motorista.longitude),
                },
                icon: {
                  url: motorista.inTrip ? `/assets/images/map/car_current_trip.png?id=${motorista.id}` : `/assets/images/map/car_normal.png?id=${motorista.id}`,
                  scaledSize: new google.maps.Size(45, 45, "px", "px"),
                } as google.maps.Icon,
                title: motorista.id,
              } as google.maps.MarkerOptions
            }
          });
        }
        else {
          this.insertOrUpdateDriverMarkers(temp);
        }
      },
        (error) => {
        }
      );
    }, 5000);
  }

  insertOrUpdateDriverMarkers(allDriverMarkers: MotoristaMarker[]) {
    if (!this.motoristaMarkers.length) {
      this.motoristaMarkers = allDriverMarkers;
      return;
    }
    this.motoristaMarkers = this.motoristaMarkers.filter(e => allDriverMarkers.find(f => f.motorista.id === e.motorista.id));

    allDriverMarkers.map(driverMarker => {
      const index = this.motoristaMarkers.findIndex(e => e.motorista.id === driverMarker.motorista.id);
      if (index > -1) {
        this.motoristaMarkers[index].motorista.latitude = driverMarker.motorista.latitude;
        this.motoristaMarkers[index].motorista.longitude = driverMarker.motorista.longitude;
        this.motoristaMarkers[index].marker.position = driverMarker.marker.position;
        if (driverMarker.motorista.inTrip) {
          const marker = this.motoristaMapMarker.find(e => e.marker.getTitle() === driverMarker.motorista.id);
          marker.marker.setIcon({
            url: `/assets/images/map/car_current_trip.png?id=${driverMarker.motorista.id}`,
            // size: new google.maps.Size(22, 50),
            // size: new google.maps.Size(30, 30, "%", "%"),
            scaledSize: new google.maps.Size(45, 45, "px", "px"),
          } as google.maps.Icon);
        }
        else {
          const marker = this.motoristaMapMarker.find(e => e.marker.getTitle() === driverMarker.motorista.id);
          marker.marker.setIcon({
            url: `/assets/images/map/car_normal.png?id=${driverMarker.motorista.id}`,
            // size: new google.maps.Size(22, 50),
            // size: new google.maps.Size(30, 30, "%", "%"),
            scaledSize: new google.maps.Size(45, 45, "px", "px"),
          } as google.maps.Icon);
        }
      } else {
        this.motoristaMarkers.push(driverMarker);
      }
    });
  }

  private getEstablishments() {
    this._crud.getData(`restaurante`).subscribe(
      async (response: EstabelecimentoResponse) => {
        this.restaurants = response.data.filter(e => e.showOnMap || e.tipoEstabelecimento === "PARTNER");
        const withGeolocationRestaurants = this.restaurants.filter(restaurant => restaurant.latitude && restaurant.longitude);
        withGeolocationRestaurants.map(restaurant => {
          this.establishmentMarkers.push({
            restaurant: restaurant,
            marker: {
              label: {
                text: restaurant['nomeRestaurante'],
                className: 'marker-label'
              },
              draggable: false,
              optimized: false,
              clickable: true,
              title: restaurant['nomeRestaurante'],
              position: {
                lat: restaurant.latitude,
                lng: restaurant.longitude,
              },
              icon: {
                url: restaurant['icone'],
                size: new google.maps.Size(50, 50),
                scaledSize: new google.maps.Size(50, 50),
                labelOrigin: new google.maps.Point(25, 25),
              } as google.maps.Icon,
            } as google.maps.MarkerOptions
          });
        });
        const noGeolocationRestaurants = this.restaurants.filter(restaurant => !restaurant.latitude || !restaurant.longitude);
        noGeolocationRestaurants.map((restaurant, i) => {
          this.timeout = setTimeout(() => {
            this._geocoder.geocode({ 'address': restaurant['endereco'] }).subscribe((response) => {
              if (response.status === 'OK') {
                this.establishmentMarkers.push({
                  restaurant: restaurant,
                  marker: {
                    label: {
                      text: restaurant['nomeRestaurante'],
                      className: 'marker-label'
                    },
                    draggable: false,
                    optimized: false,
                    clickable: true,
                    title: restaurant['nomeRestaurante'],
                    position: {
                      lat: response.results[0].geometry.location.lat(),
                      lng: response.results[0].geometry.location.lng(),
                    },
                    icon: {
                      url: restaurant['icone'],
                      size: new google.maps.Size(50, 50),
                      scaledSize: new google.maps.Size(50, 50),
                      labelOrigin: new google.maps.Point(25, 25),
                    } as google.maps.Icon,
                  } as google.maps.MarkerOptions
                });

              }
              else {
                console.log('Geocode was not successful for the following reason: ' + status);
              }
            });
          }, 850 * i);
        });
        // this.loadCtrl.dismiss();
      },
      (error) => {
        // this.loadCtrl.dismiss();
      }
    );
  }

  public async openInfoWindow(marker: MapMarker, restaurant: Estabelecimento) {
    console.log("openInfoWindow");
    console.log(marker);
    // this.infoWindow.open(marker, true);
    this.selectedRestaurant = restaurant;
    await this.modal.present();
    console.log(this.selectedRestaurant);
  }

  async closeModal() {
    await this.modal.dismiss(null, 'cancel');
  }

  async getLocationUser() {
    if (navigator.geolocation) {
      this.locationSubscription = this.watchPosition({ enableHighAccuracy: true }).subscribe(async (position) => {
        this.currentUserMarker.position = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        this.mapOptions.center.lat = this.currentUserMarker.position.lat;
        this.mapOptions.center.lng = this.currentUserMarker.position.lng;
      }, async error => {
        await this._util.showToast("Couldn't get current location. Please make sure you have location permissions enabled in your device settings.");
      });
    } else {
      console.log("No support for geolocation");
      await this._util.showToast("Couldn't get current location. Please make sure you have location permissions enabled in your device settings.");
    }
  }

  private watchPosition(options?: PositionOptions): Observable<GeolocationPosition> {
    return new Observable<GeolocationPosition>((subscriber: Subscriber<GeolocationPosition>): TeardownLogic => {
      this.watchId = navigator.geolocation.watchPosition(
        subscriber.next.bind(subscriber),
        subscriber.error.bind(subscriber),
        options,
      );
      return (): void => {
        navigator.geolocation.clearWatch(this.watchId);
      };
    });
  }

  async goToRestaurantBooking() {
    sessionStorage.setItem("restaurantMap", JSON.stringify(this.selectedRestaurant));
    await this.modal.dismiss(null, 'cancel');
    await this._router.navigate(["call", "restaurants-booking"]);
  }

  async showLoading() {
    mapLoadingProps.mode = this._platform.is("ios") ? "ios" : "md";
    const loading = await this._loadCtrl.create(mapLoadingProps);
    return await loading.present();
  }

}
